import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import { fetchBonusTransaction, selectBonusTransaction, selectBonusTransactionCheck } from "../model"
import { PageSubtitle } from "@/shared/ui/page-subtitle"
import { PageHeader } from "@/widgets/page-header"
import { fromNanoToLocaleBalance, selectBonuses } from "@/entities/bonus"
import BonusSvg from '@/shared/assets/images/BonusMenuIcon.svg'
import { getOperationType } from "@/entities/bonus/lib"
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters"

export const BonusTransactionPage = () => {
    const { id } = useParams()
    const bonuses = useAppSelector(selectBonuses)
    const transaction = useAppSelector(selectBonusTransaction)
    const checks = useAppSelector(selectBonusTransactionCheck)
    const check = checks[transaction.check_id]
    const dispatch = useAppDispatch()

    const transactionBonus = useMemo(() => bonuses.find(bonus => bonus.id === transaction.bonus_id), [bonuses, transaction])
    const checkSum = useMemo(() => check?.data?.items.reduce((acc, curr) => acc += curr.price, 0).toFixed(2), [check])
    const bonusSum = useMemo(() => check?.data?.items.reduce((acc, curr) => acc += curr.bonus, 0).toFixed(2), [check])

    useEffect(() => {
        if (id !== transaction.id) {
            dispatch(fetchBonusTransaction(id))
        }
    }, [id])

    return (
        <div className="_page_container">
            <PageHeader title="детали транзакции" />
            {transaction &&
                <div className='flexbox'>
                    <PageSubtitle
                        variant="acid"
                        title={
                            <div className="w-full flex items-center justify-between">
                                <div>
                                    Сумма транзакции
                                </div>
                                <div className="flex items-center gap-[6px] py-[6px] px-3 bg-color-acid rounded-full">
                                    <div className="text-color-dark font-manrope normal-case font-semibold">
                                        {fromNanoToLocaleBalance(transaction.amount_nano)}
                                    </div>
                                    <div className="fill-color-acid text-color-dark">
                                        <BonusSvg width={24} height={24} />
                                    </div>
                                </div>
                            </div>
                        }
                    />
                    <div className="box">
                        <div className="box_header !mb-4 screen:!mb-[22px] font-mediumttf">
                            детали транзакции
                        </div>
                        <div className="flex flex-col">
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">Бонус</div>
                                <div className="flex gap-2 items-center">
                                    <div className="w-[36px] h-[36px] rounded-full border border-color-dark">
                                        <img src={transactionBonus?.image} alt="" />
                                    </div>
                                    <div className="text-[13px] text-color-dark">
                                        {transactionBonus?.name}
                                    </div>
                                </div>
                            </div>
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">Номер транзакции</div>
                                <div className="text-[13px] text-color-dark">
                                    {transaction.id}
                                </div>
                            </div>
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">Дата операции</div>
                                <div className="text-[13px] text-color-dark">
                                    {toLocaleDateTimeString(transaction.created_date)}
                                </div>
                            </div>
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">Тип операции</div>
                                <div className="text-[13px] text-color-dark">
                                    {getOperationType(transaction)}
                                </div>
                            </div>
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">ID Отправителя</div>
                                <div className="text-[13px] text-color-dark">
                                    {transaction.from.client_id
                                        ? transaction.from.client_id
                                        : transaction.from.system_id
                                    }
                                </div>
                            </div>
                            <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                <div className="text-[#828282] text-[12px] mb-2 uppercase">ID Получателя</div>
                                <div className="text-[13px] text-color-dark">
                                    {transaction.to.client_id
                                        ? transaction.to.client_id
                                        : transaction.to.system_id
                                    }
                                </div>
                            </div>
                            {Boolean(transaction.accruals?.length) && (
                                <div className="pb-[10px] border-b mb-3 border-[#B9C9C6]">
                                    <div className="text-[#828282] text-[12px] mb-2 uppercase">Правила начисления</div>
                                    <div className="flex flex-col gap-2 text-[13px] text-color-dark">
                                        {transaction.accruals?.map(accrual => (
                                            <div key={accrual.partner_accrual_id + accrual.bonus_id}>
                                                <div>{accrual.partner_accrual_name || "—"}</div>
                                                <div className="text-[#828282]">{accrual.partner_accrual_id}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {check &&
                        <div className="box">
                            <div className="box_header !mb-4 screen:!mb-[22px] font-mediumttf">
                                Электронный чек
                            </div>
                            <table className='w-full'>
                                <thead>
                                    <tr className="text-[#828282] text-[13px] uppercase font-semibold font-manrope">
                                        <th className="border-b border-[#E7EAEE] !text-start px-2 py-5">Кол-во</th>
                                        <th className='border-b border-[#E7EAEE] !text-start px-2 py-5'>Название</th>
                                        <th className='border-b border-[#E7EAEE] !text-start px-2 py-5'>Цена</th>
                                        <th className='border-b border-[#E7EAEE] !text-start px-2 py-5'>Скидка</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {check.data.items.map((item, index) =>
                                        <tr key={index}>
                                            <td className='text-nowrap h-full px-2 py-5 text-[13px] font-manrope font-semibold text-color-dark border-[#E7EAEE] border-b w-auto'>
                                                {item.quantity} {item.measure}.
                                            </td>
                                            <td className="text-nowrap h-full px-2 py-5 text-[13px] font-manrope font-semibold text-color-dark border-[#E7EAEE] border-b w-auto">
                                                <div className='inline-block overflow-hidden text-ellipsis w-full pr-2 text-nowrap'>
                                                    {item.name}
                                                </div>
                                            </td>
                                            <td className='text-nowrap h-full px-2 py-5 text-[13px] font-manrope font-semibold text-color-dark border-[#E7EAEE] border-b w-auto'>
                                                {item.price}
                                            </td>
                                            <td className='text-nowrap h-full px-2 py-5 text-[13px] font-manrope font-semibold text-color-dark border-[#E7EAEE] border-b w-auto'>
                                                {item.bonus}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tr className='border-t border-b'>
                                    <td className='text-nowrap h-full px-2 py-5 text-[13px] font-manrope font-semibold text-color-dark border-[#E7EAEE] border-b w-auto'>ИТОГ</td>
                                    <td className="text-nowrap h-full px-2 py-5 text-[13px] font-manrope font-semibold text-color-dark border-[#E7EAEE] border-b w-auto"></td>
                                    <td className="text-nowrap h-full px-2 py-5 text-[13px] font-manrope font-semibold text-color-dark border-[#E7EAEE] border-b w-auto">{checkSum}</td>
                                    <td className="text-nowrap h-full px-2 py-5 text-[13px] font-manrope font-semibold text-color-dark border-[#E7EAEE] border-b w-auto">{bonusSum}</td>
                                </tr>
                            </table>
                            <div className='grid grid-cols-12 mt-4 gap-y-4'>
                                <div className='col-span-6 md:col-span-3 font-bold'>
                                    ФН ККТ
                                </div>
                                <div className='col-span-6 md:col-span-3'>
                                    {check.data.kkm_info.serial_number}
                                </div>
                                <div className='col-span-6 md:col-span-3 font-bold'>
                                    ФП ККТ
                                </div>
                                <div className='col-span-6 md:col-span-3'>
                                    {check.data.kkm_info.fiscal_sign}
                                </div>
                                <div className='col-span-6 md:col-span-3 font-bold'>
                                    ФД ККТ
                                </div>
                                <div className='col-span-6 md:col-span-9'>
                                    {check.data.kkm_info.document_number}
                                </div>
                                <div className='col-span-6 md:col-span-3 font-bold'>
                                    RRN
                                </div>
                                <div className='col-span-6 md:col-span-3'>
                                    {check.data.bank_info.rrn}
                                </div>
                                <div className='col-span-6 md:col-span-3 font-bold'>
                                    M
                                </div>
                                <div className='col-span-6 md:col-span-3'>
                                    {check.data.bank_info.merchant_id}
                                </div>
                                <div className='col-span-6 md:col-span-3 font-bold'>
                                    T
                                </div>
                                <div className='col-span-6 md:col-span-9'>
                                    {check.data.bank_info.terminal_id}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    )
}
